<template>
  <div class="foundry-contract-table" :data-qa-id="`foundry-contract-popup-table-${status}`">
    <agGrid
      :rowData="foundries"
      :columnDefs="columnDefs"
      :isPagination="false"
      :enableCellTextSelection="true"
      noRowsToShowText=""
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { MdTypography, MdIconClock } from '@antiqua-dls/vue-components';
import agGrid from 'components/agGrid/index.vue';
import HeaderRenderer from 'components/agGridRenderer/header.vue';

const { t } = useI18n();

const MOBILE_BREAKPOINT = 719;
const isMobile = ref(window.innerWidth <= MOBILE_BREAKPOINT);

const onResize = () => {
  isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
};

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const props = defineProps({
  foundries: {
    type: Array,
    required: true
  },
  showLeavingIn: {
    type: Boolean,
    default: false
  },
  sortable: {
    type: Boolean,
    default: true
  },
  status: {
    type: String,
    default: ''
  }
});

const CountdownBadgeRenderer = (params) => {
  const days = params.value;
  if (!days) return '';

  const wrapper = document.createElement('span');
  wrapper.className = 'foundry-contract-table__countdown-badge';
  wrapper.style.cssText = 'position: absolute; top: 50%; transform: translateY(-50%);';

  const icon = document.createElement('md-icon-clock');
  icon.setAttribute('size', '12px');
  icon.setAttribute('color', 'red-babe--700');
  wrapper.appendChild(icon);

  const text = document.createElement('span');
  text.className = 'foundry-contract-table__countdown-text';
  text.textContent = `${days} ${days === 1 ? 'day' : 'days'}`;
  wrapper.appendChild(text);

  return wrapper;
};

const columnDefs = computed(() => {
  const cols = [
    {
      headerName: t('foundry_contract_popup.table_header_foundry'),
      field: 'name',
      headerComponent: HeaderRenderer,
      headerComponentParams: {
        fieldName: 'foundry-name',
        headerText: t('foundry_contract_popup.table_header_foundry'),
        sortEnabled: props.sortable
      },
      suppressMovable: true,
      resizable: false,
      sortable: props.sortable,
      flex: 1
    },
    {
      headerName: t('foundry_contract_popup.table_header_termination_date'),
      field: 'terminationDate',
      headerComponent: HeaderRenderer,
      headerComponentParams: {
        fieldName: 'termination-date',
        headerText: t('foundry_contract_popup.table_header_termination_date'),
        sortEnabled: props.sortable
      },
      suppressMovable: true,
      resizable: false,
      sortable: props.sortable,
      width: 200
    }
  ];

  if (props.showLeavingIn && !isMobile.value) {
    cols.push({
      headerName: t('foundry_contract_popup.table_header_leaving_in'),
      field: 'daysLeft',
      headerComponent: HeaderRenderer,
      headerComponentParams: {
        fieldName: 'leaving-in',
        headerText: t('foundry_contract_popup.table_header_leaving_in'),
        sortEnabled: false
      },
      suppressMovable: true,
      resizable: false,
      sortable: false,
      width: 160,
      cellRenderer: CountdownBadgeRenderer
    });
  }

  return cols;
});
</script>

<style lang="scss">
.foundry-contract-table {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  .ag-theme-overrides {
    padding: 0;
    width: 100%;
    min-width: 0;
  }

  .ag-root-wrapper {
    border: 1px solid var(--md-border-grey);
    border-radius: 8px;
    overflow: hidden;
  }

  .foundry-contract-table__countdown-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: var(--md-color--red-babe--100);
    padding: 2px 8px;
    border-radius: 4px;
    height: 24px;
    box-sizing: border-box;
    white-space: nowrap;
  }

  .foundry-contract-table__countdown-text {
    font-size: 11px;
    font-weight: 500;
    color: var(--md-color--red-babe--700);
    line-height: 16px;
  }
}
</style>
